// v1.2.0 — M® Note: the seam under the screens.
//
// Everything the app needs to send, check and take an M® Note, with no UI and
// no Worker in it. The screens (the UI session's) call this; the live source is
// ArcNoteSource (MNote v2.2 on Arc). Words a person reads stay in the screens —
// here the contract's words are used: issue, transfer, redeem, controller.
//
// SEND (locked or cash), on the M® Note card:
//   const s = newSecret("code");                       // or "link" when unticked
//   const d = await derive(kdfInput(s.kind, s.secret), s.salt);   // ~0.5 s, off the main thread
//   const calls = source.issueCalls(amount, d.address, s.salt);   // approve + issueNote
//   const prepared = await adapter.prepare(calls);                 // while the card is shown
//   keepSent({ controller: d.address, salt: s.salt, kind: s.kind, secret: s.secret, amount, at: Date.now() });
//   const sub = await adapter.sendPrepared(prepared);              // the slide: one Face ID
//   patchSent(d.address, { userOpHash: sub.userOpHash });
//   await sub.settled;                                             // the link needs Arc
//   const note = await settleSent(source, d.address);              // → the serial
//   noteLink(location.origin, note.serial, s.kind === "link" ? s.secret : undefined)
// keepSent comes BEFORE the signature on purpose: a note whose secret was
// never written down can't be taken by anyone. A cancelled Face ID → forgetSent.
// The card with the link can only be shown after `settled`: the serial is the
// contract's counter, assigned when Arc mines the note, not before.
//
// TAKE, on the page at /A0000040Z5:
//   const serial = serialFromPath(location.href);
//   const note = await source.bySerial(serial);        // one read; poll it for the status line
//   // cash link: secretFromFragment(location.hash); locked: parseCode(typed) → secretFromCode
//   const d = await unlock(note, secret);              // null = wrong secret; nothing left the phone
//   const calls = await source.takeCalls(note, d.key, myWallet);   // take + cash in: two calls, one userOp
//   adapter.prepare(calls) → the button → adapter.sendPrepared
// After it the taker holds dollars, not a note: the balance goes up by the
// note's value and the statement shows it as money in from an M® Note.
// Derive when the twelfth symbol passes parseCode, not on the tap: the tap must
// go straight to Face ID (iOS drops the gesture after half a second of work).
// Re-make the calls with every re-prepare; the signature inside is good for
// ten minutes and can only ever move the note to `myWallet`.

import type { Hex } from "viem";
import { newCode, newLinkSecret, newSalt, secretFromCode } from "./code";
import { derive, type Derived } from "./kdf";
import { linkPath, type Serial } from "./serial";
import { ArcNoteSource } from "./ArcNoteSource";

export { NotesUnavailableError, dollarsOf, toUnits, NOTE_FEE_DEFAULT, feeUnits, type NoteFeeCalls } from "./ArcNoteSource";

export { derive, type Derived } from "./kdf";

/** One contract call, in the adapter's shape (prepare / sendPrepared / submit). */
export interface Call { to: string; data: string }

export type NoteStatus = "live" | "spent" | "redeemed";

/** A note as Arc has it. */
export interface NoteView {
  id: Hex;
  serial: Serial;
  /** Dollars, with cents (14.5 for $14.50; series A is whole dollars). For display; arithmetic uses `units`. */
  value: number;
  /** v0.5 — the value exactly, in the token's units (6 dp): 14500000 for $14.50. */
  units: bigint;
  /** "USD" for series A and B — the series names the token. */
  currency: string;
  controller: Hex;
  version: bigint;
  status: NoteStatus;
  /** The KDF salt of a secret-held note; zero for a note issued straight to a wallet. */
  salt: Hex;
}

export interface NoteSource {
  /** True when this build can issue notes (VITE_NOTES_ADDRESS names a known series). Reading and taking never depend on it. */
  readonly live: boolean;
  /** The note a serial stands for — one read from Arc, never a cache. null = no such note. */
  bySerial(serial: Serial): Promise<NoteView | null>;
  /** Every live note an address controls (a wallet, or a secret's address), in one read per series. */
  heldBy(address: string): Promise<NoteView[]>;
  /** 1.4.55 — the currencies this build can issue a note in: the series VITE_NOTES_ADDRESS names, and every series marked `issue`. */
  issuingCurrencies(): string[];
  /** approve + issueNote — one userOp. `controller` is derive(...).address (bearer) or a wallet with salt 0x00…00 (designated).
   *  `currency` (1.4.55) picks the series; default the one VITE_NOTES_ADDRESS names.
   *  `fee` (1.4.68–1.4.71): the M® Note fee — USDC to the key's address, so the key can cash the note in by itself
   *  (keyTake.ts), after its conversion calls when it is paid from another currency. */
  issueCalls(amount: number, controller: string, salt: Hex, currency?: string, opts?: { fee?: import("./ArcNoteSource").NoteFeeCalls }): Call[];
  /**
   * The take, for the taker's own wallet to submit as ONE userOp: the secret's
   * key signs the note over to the taker (transferNoteBySig), and the taker's
   * wallet cashes it in to itself (redeemNote) — so "Put it in your wallet"
   * ends with dollars in the wallet and the balance up (v0.4; decided 2 Oct).
   * The note ends there: its serial reads Redeemed from then on.
   * `keep: true` leaves it a note in the taker's wallet instead (not used by
   * the app in v1; the only way to take part of a note, with `amount`).
   */
  takeCalls(note: NoteView, key: Hex, taker: string, opts?: { keep?: boolean; amount?: number; now?: number }): Promise<Call[]>;
  /** A wallet handing on a note it controls (whole, or `amount` of it). */
  handOverCalls(note: NoteView, to: string, amount?: number): Call[];
  /** A wallet turning a note it controls back into USDC at `to` — the ladder of sources batches this with the payment. */
  redeemCalls(note: NoteView, to: string, amount?: number): Call[];
}

let source: NoteSource | null = null;
/** The app's note source. One instance; it opens no connection until the first read. */
export function noteSource(): NoteSource {
  return (source ??= new ArcNoteSource());
}

// ------------------------------------------------------------ the secret

/** How the secret travels: a code said aloud (locked), or in the link after # (cash). */
export type SecretKind = "code" | "link";

export interface NewSecret {
  kind: SecretKind;
  /** As written: the 12-symbol code (unformatted), or the 16 symbols that go after #. */
  secret: string;
  /** 32 random bytes for issueNote; public. */
  salt: Hex;
}

export function newSecret(kind: SecretKind): NewSecret {
  return { kind, secret: kind === "code" ? newCode() : newLinkSecret(), salt: newSalt() };
}

/** What goes into the KDF: a code without its check symbol; a link secret as it is. */
export function kdfInput(kind: SecretKind, written: string): string {
  return kind === "code" ? secretFromCode(written) : written;
}

/**
 * The note's link: the serial, and for a cash link the secret. Never the value, never a code.
 * app 1.4.46 (Alexander, 19 Oct) — the secret is a second path segment, "/A0000040Z5/<secret>",
 * no longer after `#`: Snapchat hands a link on without its `#` part, and the take stopped there.
 * Links made before, with `#`, still open (serialFromPath, the Take page, take.mjs, the Toolbox).
 */
export function noteLink(origin: string, serial: Serial | string, linkSecret?: string): string {
  return origin.replace(/\/+$/, "") + linkPath(serial) + (linkSecret ? "/" + linkSecret : "");
}

export const ZERO_SALT: Hex = `0x${"00".repeat(32)}`;
const same = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

/** Does this derived key control this note right now? */
export function opens(note: NoteView, d: Pick<Derived, "address">): boolean {
  return note.status === "live" && same(note.controller, d.address);
}

/**
 * The Take page's test. `secret` is the KDF input (kdfInput / secretFromCode /
 * secretFromFragment). Returns the key when the secret opens the note, null
 * when it doesn't. Runs the KDF (~0.5 s, in a Worker); sends nothing anywhere.
 */
export async function unlock(note: NoteView, secret: string): Promise<Derived | null> {
  if (note.status !== "live" || same(note.salt, ZERO_SALT)) return null;
  const d = await derive(secret, note.salt);
  return opens(note, d) ? d : null;
}

/**
 * "Not in anyone's wallet yet", read from the note alone (before any code is
 * typed). Exact for everything the app does in v1: a note issued to a secret
 * is live, salted and at version 1 until someone takes it whole. Not exact
 * for the child of a partial operation, which inherits the salt at version 1
 * while sitting in a wallet — the chain keeps no flag for "held by a secret".
 * The sender's own device knows for certain (its record's controller), and so
 * does anyone holding the secret (unlock).
 */
export function looksUntaken(note: NoteView): boolean {
  return note.status === "live" && note.version === 1n && !same(note.salt, ZERO_SALT);
}

// ------------------------------------------------------------ what this device sent
//
// Every note this wallet sent from this browser. While nobody has taken it the
// record holds its link and its code — "share again, never take back". Kept
// in this browser only, as Drop keys are (m.dropKeys): never synced, never
// sent to a server. The key name must be in walletSwitch.ts WALLET_KEYS (a
// wallet switch puts it aside) and must NOT be in settings.ts SYNCED.
// The moment Arc shows the note has moved, the SECRET is deleted; the record
// stays, marked taken, so the row can read "Taken" (v0.3.1). Only the newest
// MAX_TAKEN taken records are kept here — older ones are the statement's.

export const NOTES_KEY = "m.notes";

export interface SentNote {
  /** The address the secret stands for — known before the note exists, so it keys the record. */
  controller: Hex;
  salt: Hex;
  kind: SecretKind;
  /** As written: the 12-symbol code, or the 16-symbol link secret. Gone once the note is taken. */
  secret?: string;
  /** The amount, with cents (v0.5) — in `currency`. */
  amount: number;
  /** 1.4.55 — the note's currency; absent on records from before the second series (a dollar note). */
  currency?: string;
  /** Date.now() when the card was slid. */
  at: number;
  /** Set at the bundler's acceptance. */
  userOpHash?: string;
  /** Set once Arc has mined the note: the ten symbols of its serial, and its id. */
  serial?: string;
  id?: Hex;
  /**
   * Set when this browser first saw that the note had moved. It is when the
   * browser LOOKED, not when the note was taken — the chain keeps no time for
   * that; Worker 1.1.0 will. A record with takenAt has no secret.
   */
  takenAt?: number;
}

/** "sent" — nobody has taken it yet (Copy code / Copy link live here); "taken" — it has moved. */
export type SentState = "sent" | "taken";
export const sentState = (rec: SentNote): SentState => (rec.takenAt ? "taken" : "sent");

/** How many taken records this browser keeps (the newest by takenAt). Untaken records are never dropped for room. */
export const MAX_TAKEN = 20;

type Store = Pick<Storage, "getItem" | "setItem">;
const memory = new Map<string, string>();
const fallbackStore: Store = {
  getItem: (k) => memory.get(k) ?? null,
  setItem: (k, v) => void memory.set(k, v),
};
const defaultStore = (): Store => {
  try {
    if (typeof localStorage !== "undefined") return localStorage;
  } catch {
    /* storage blocked */
  }
  return fallbackStore;
};

export function listSent(store: Store = defaultStore()): SentNote[] {
  try {
    const v = JSON.parse(store.getItem(NOTES_KEY) ?? "[]");
    return Array.isArray(v) ? (v as SentNote[]) : [];
  } catch {
    return [];
  }
}
const write = (all: SentNote[], store: Store) => store.setItem(NOTES_KEY, JSON.stringify(all));

/** Write a note's secret down BEFORE it is signed for. Replaces a record with the same controller. */
export function keepSent(rec: SentNote, store: Store = defaultStore()): void {
  write([rec, ...listSent(store).filter((r) => !same(r.controller, rec.controller))], store);
}

export function getSent(controller: string, store: Store = defaultStore()): SentNote | null {
  return listSent(store).find((r) => same(r.controller, controller)) ?? null;
}

export function patchSent(controller: string, patch: Partial<Omit<SentNote, "controller">>, store: Store = defaultStore()): void {
  write(listSent(store).map((r) => (same(r.controller, controller) ? { ...r, ...patch } : r)), store);
}

/** Two dollar amounts are the same note value when they are the same number of cents (v0.5: 14.5 vs 14.499999…). */
export function sameAmount(a: number, b: number): boolean {
  return Math.round(a * 100) === Math.round(b * 100);
}

/** Drop a record and its secret altogether: the slide was cancelled, or there never was a note. */
export function forgetSent(controller: string, store: Store = defaultStore()): void {
  write(listSent(store).filter((r) => !same(r.controller, controller)), store);
}

/**
 * After the receipt: find the note Arc minted for this record and write its
 * serial down. null = Arc doesn't show it (yet) — try again, or leave it to
 * reconcileSent.
 */
export async function settleSent(src: NoteSource, controller: string, store: Store = defaultStore()): Promise<NoteView | null> {
  const rec = getSent(controller, store);
  const held = await src.heldBy(controller);
  const note = held.find((n) => !rec || sameAmount(n.value, rec.amount)) ?? held[0] ?? null;
  if (note && rec) patchSent(controller, { serial: note.serial.text, id: note.id }, store);
  return note;
}

const NEVER_SENT_MS = 10 * 60_000; // slid, never accepted (a cancelled or failed Face ID that wasn't cleaned up)
const NEVER_MINED_MS = 24 * 3_600_000; // accepted, and a day later Arc still shows no note: it didn't go through

export interface SentRow {
  sent: SentNote;
  state: SentState;
  /** The note as Arc has it now; undefined when it is taken, not mined yet, or the read failed. */
  note?: NoteView;
}

/**
 * Bring the records in line with Arc — on the home's mount and on return to
 * the screen. One read per record that isn't taken yet; a taken record is
 * never read again. A note that was taken (or otherwise moved) loses its
 * secret and is marked taken; a record whose note never appeared is dropped
 * after a while; a failed read changes nothing. Returns every record, in the
 * store's order (the newest slide first), each with its state.
 */
export async function reconcileSent(src: NoteSource, opts: { store?: Store; now?: number } = {}): Promise<SentRow[]> {
  const store = opts.store ?? defaultStore();
  const now = opts.now ?? Date.now();
  const rows: SentRow[] = [];
  for (const rec of listSent(store)) {
    if (rec.takenAt) {
      rows.push({ sent: rec, state: "taken" });
      continue;
    }
    try {
      const held = await src.heldBy(rec.controller);
      // The same note; or, after a partial take, the remainder the secret still holds.
      const note = held.find((n) => n.id === rec.id) ?? held.find((n) => sameAmount(n.value, rec.amount)) ?? held[0];
      if (note) {
        const cur = { ...rec, serial: note.serial.text, id: note.id, amount: note.value };
        if (cur.serial !== rec.serial || cur.id !== rec.id || !sameAmount(cur.amount, rec.amount)) {
          patchSent(rec.controller, { serial: cur.serial, id: cur.id, amount: cur.amount }, store);
        }
        rows.push({ sent: cur, state: "sent", note });
      } else if (rec.serial) {
        // It was there and it has moved: taken. The secret goes now; the row stays.
        const { secret: _gone, ...kept } = rec;
        const taken: SentNote = { ...kept, takenAt: now };
        write(listSent(store).map((r) => (same(r.controller, rec.controller) ? taken : r)), store);
        rows.push({ sent: taken, state: "taken" });
      } else if (now - rec.at > (rec.userOpHash ? NEVER_MINED_MS : NEVER_SENT_MS)) {
        forgetSent(rec.controller, store); // there never was a note
      } else {
        rows.push({ sent: rec, state: "sent" });
      }
    } catch {
      rows.push({ sent: rec, state: "sent" }); // Arc couldn't be read: say nothing new, delete nothing
    }
  }
  // Room: keep the newest MAX_TAKEN taken records; never drop an untaken one.
  const stale = new Set(
    rows.filter((r) => r.state === "taken").sort((a, b) => b.sent.takenAt! - a.sent.takenAt!).slice(MAX_TAKEN).map((r) => r.sent.controller.toLowerCase()),
  );
  if (stale.size) {
    write(listSent(store).filter((r) => !stale.has(r.controller.toLowerCase())), store);
    return rows.filter((r) => !stale.has(r.sent.controller.toLowerCase()));
  }
  return rows;
}
